import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary/Default - Vibrant teal with white text (always readable)
        default: "bg-[#1EAFA0] text-white hover:bg-[#1A9A8D] active:bg-[#178A7E] shadow-md hover:shadow-lg focus-visible:ring-[#1EAFA0]/40 disabled:bg-[#1EAFA0] disabled:text-white/70",

        // Destructive - Red with white text (always readable)
        destructive: "bg-red-600 text-white hover:bg-red-500 active:bg-red-700 shadow-md disabled:bg-red-600 disabled:text-white/70 disabled:opacity-70",

        // Outline - Border with foreground text, subtle background on hover
        outline: "border-2 border-input bg-transparent text-foreground hover:bg-accent/10 hover:text-foreground hover:border-accent/50 active:bg-accent/15 shadow-sm disabled:border-input/50 disabled:text-foreground/50",

        // Secondary - Soft background with dark text
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/70 active:bg-secondary/60 shadow-sm disabled:bg-secondary disabled:text-secondary-foreground/70",

        // Ghost - Transparent with subtle hover
        ghost: "text-foreground hover:bg-accent/10 hover:text-foreground active:bg-accent/15 disabled:text-foreground/50",

        // Link - Underlined text
        link: "text-primary underline-offset-4 hover:underline hover:text-primary/80 active:text-primary/70 disabled:text-primary/50",

        // Accent - Warm orange with white text (always readable)
        accent: "bg-[#F97116] text-white hover:bg-[#E06610] active:bg-[#C75A0E] shadow-md hover:shadow-lg focus-visible:ring-[#F97116]/40 disabled:bg-[#F97116] disabled:text-white/70",

        // Giveaway-specific variants with guaranteed contrast
        entry: "bg-gradient-accent text-white shadow-lg hover:shadow-accent-glow hover:scale-[1.02] active:scale-[0.98] font-bold disabled:text-white/70",

        entryOutline: "border-2 border-accent bg-transparent text-accent hover:bg-accent hover:text-white active:bg-accent/90 active:text-white shadow-sm disabled:border-accent/50 disabled:text-accent/50",

        success: "bg-gradient-success text-white shadow-lg font-bold hover:opacity-90 active:opacity-80 disabled:text-white/70",

        hero: "bg-gradient-hero text-white shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98] font-bold disabled:text-white/70",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-12 rounded-lg px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
